import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Inject,
  inject,
  Input,
  NgZone,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { retrieveAllErrorMessages } from '@core/analytics';
import { AnalyticsService } from '@core/analytics/analytics.service';
import { AppConfigService } from '@core/app-config';
import { FormSubmitterService } from '@core/form-submitter/form-submitter.service';
import { WINDOW } from '@core/injection-tokens';
import { CookieStorageService } from '@core/storage/cookie-storage.service';
import { TextContentService } from '@core/text-content/text-content.service';
import { WindowService } from '@core/window-actions';
import { BookerAirFormComponent } from '@dynamic-components/booker-v2/bookers/air/booker-air-form';
import { BookerMultiCityStore } from '@dynamic-components/booker-v2/bookers/air/booker-air-multi-city/store/booker-multi-city.store';
import {
  BOOKER_SUBMIT_BUTTON_THEME_MAP,
  ButtonThemeType,
} from '@dynamic-components/booker-v2/types/booker-submit-theme-type';
import {
  BOOKER_CONFIG,
  BookerFormStateWithControls,
  isInternational,
  pluckRecentAirport,
  recentSearchToUrlParam,
  sendBookerFSEvent,
  transformDynamicBookerData,
} from '@shared/booker';
import {
  convertBookerFormDataToQueryString,
  createAirBookerFormV2,
  createMobileBookingUrlMainBooker,
  createVanillaFormData,
  formDataToSimpleAirSearchData,
  getAirBookerWarnings,
  getAppropriateDateString,
  getMultiCityBookerWarnings,
  RecursivePartial,
  setCaptchaValidation,
} from '@shared/booker/bookers/booker-air/booker-air.utils';
import { JBBookerAirWarningDialogComponent } from '@shared/booker/submits/booker-air-submit/booker-air-warning-dialog';
import {
  BookerFormState,
  BookerResponse,
  BookerType,
  WarningMessage,
} from '@shared/booker/types';
import {
  BookerFlight,
  BookerFlightWithControls,
} from '@shared/booker/types/booker-flight.type';
import { BookerFormErrorObject } from '@shared/booker/types/booker-form-error-object.type';
import { BOOKER_TRIP_TYPES } from '@shared/booker/types/booker-trip-type.enum';
import { waitUntil } from '@shared/rxjs-operators/wait-until.operator';
import { RecentAirSearch } from '@shared/types';
import { CurrencySymbolPipe } from '@shared/ui/pipes/currency-symbol';
import { RWBABTestingService } from '@shared/ui/rwb-ab-testing-service/rwb-ab-testing.service';
import { getControlVal } from '@shared/ui/utils/form-control-utils/get-control-val';
import { showValidation } from '@shared/ui/utils/form-control-utils/show-validation';
import { booleanStringCheck } from '@shared/ui/utils/global-utils/boolean-string-check';
import { generateUniqueId } from '@shared/ui/utils/global-utils/generate-unique-id';
import { traverse } from '@shared/ui/utils/global-utils/traverse';
import { AnalyticsFacade } from '@store/analytics/analytics.facade';
import { BookerFacade } from '@store/booker/booker.facade';
import { TripType } from '@store/cart/types/cart.enum';
import { GlobalFacade } from '@store/global/global.facade';
import { isCityPairRestricted } from '@store/origins/api';
import { OriginsFacade } from '@store/origins/origins.facade';
import { Airport, CountryWithAirports } from '@store/origins/types';
import { RouterFacade } from '@store/router/router.facade';
import { isSameDay, isValid, parseISO } from 'date-fns';
import { JbDialogService } from 'jb-component-library';
import { ObservableInput } from 'ngx-observable-input';
import { equals, isEmpty } from 'ramda';
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  merge,
  Observable,
  of,
  timer,
  zip,
} from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  first,
  map,
  pairwise,
  share,
  skip,
  skipUntil,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { BookerAirFormService } from '../service/booker-air-form.service';
import { BookerAirStore } from '../store/booker-air.store';
@Component({
  selector: 'dot-booker-air',
  templateUrl: './booker-air.component.html',
  styleUrls: ['./booker-air.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    BookerMultiCityStore,
    BookerAirStore,
    BookerAirFormService,
    BookerFacade,
    CurrencySymbolPipe,
  ],
})
export class BookerAirComponent implements OnInit {
  // required for use of takeUntilDestroyed inside ngOnInit
  private _destroy = inject(DestroyRef);

  contentVar: BookerResponse;
  buttonThemeVar: ButtonThemeType;

  @ViewChild(BookerAirFormComponent) bookerV2Component: BookerAirFormComponent;

  isStatic: boolean;

  @Input() set buttonTheme(theme: ButtonThemeType) {
    this.buttonThemeVar = theme;
    this.setButtonTheme();
  }

  @Input() set content(content: BookerResponse) {
    this.contentVar = content;
    this.bookerAirStore.updateScheduleEnabled(
      booleanStringCheck(content?.showSchedule),
    );
    this.bookerAirStore.updatePricingEnabled(
      booleanStringCheck(content?.showPricingInCalendar),
    );
    this.removeRouteFiltering = booleanStringCheck(
      content?.removeRouteFiltering,
    );

    this.setButtonTheme();
    this.bookerAirStore.updateContent(content);
    this.bookerAirStore.updateDateAggregator(content.dateAggregator);
  }

  @ObservableInput()
  @Input('tripType')
  tripType$: Observable<BOOKER_TRIP_TYPES>;
  @ObservableInput() @Input('isTrueBlue') isTrueBlue$: Observable<boolean>;
  @ObservableInput()
  @Input('destinationAirport')
  destinationAirport$: Observable<string>;
  @ObservableInput() @Input('originAirport') originAirport$: Observable<string>;
  @ObservableInput() @Input('departDate') departDate$: Observable<string>;
  @ObservableInput() @Input('returnDate') returnDate$: Observable<string>;
  @ObservableInput() @Input('promoCode') promoCode$: Observable<string>;
  @ObservableInput()
  @Input('travelerCountAdult')
  travelerCountAdult$: Observable<number>;
  @ObservableInput()
  @Input('travelerCountChild')
  travelerCountChild$: Observable<number>;
  @ObservableInput()
  @Input('travelerCountInfant')
  travelerCountInfant$: Observable<number>;

  @ObservableInput() @Input('multiFlights') multiFlights$: Observable<
    { origin: string; destination: string; departDate: string }[]
  >;

  @Input() saveToUrlParam = false;
  @Input() disableAutoSubmit: 'true' | 'false' | boolean = false;

  @Output() jbBookerSubmit: EventEmitter<unknown> = new EventEmitter();
  @Output() jbBookerInitialStateSet: EventEmitter<BookerFormState> =
    new EventEmitter();

  tripTypeEnum = TripType;
  showCaptcha = false;
  // TO DO - remove this when captchaInvalid is available in content
  captchaErrorMessage: string;

  // booker form initialization with values from BOOKER_CONFIG
  form = this.createForm();
  // this will be true first time formData is saved in localstorage
  isFormDataSet = false;

  isMobile$ = this.windowService.isMobile;
  regions$ = this.originsFacade.regions;
  airportMap$ = this.originsFacade.airportsMap;
  bookerData$: Observable<BookerResponse> = this.textContentService
    .getJsonAsset('booker-response-text')
    .pipe(share());

  showFormErrors$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false,
  );

  departureDateError$: BehaviorSubject<string> = new BehaviorSubject<string>(
    '',
  );
  returnDateError$: BehaviorSubject<string> = new BehaviorSubject<string>('');
  travelerCountError$: BehaviorSubject<string> = new BehaviorSubject<string>(
    '',
  );
  originAirportError$: BehaviorSubject<string> = new BehaviorSubject<string>(
    '',
  );
  destinationAirportError$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');

  updateRecentReturnDate$: BehaviorSubject<Date | null> =
    new BehaviorSubject<Date | null>(null);

  updateRecentDepartureDate$: BehaviorSubject<Date | null> =
    new BehaviorSubject<Date | null>(null);

  recentDepartureDate$;
  recentReturnDate$;

  promoCodeWithSource$;
  recentTravellerCount$;
  evaluatedIsTrueBlue$: Observable<boolean>;
  evaluatedTripType$: Observable<string>;
  origin$: Observable<Airport>;
  destination$: Observable<Airport>;
  isOdServiceDown$: Observable<boolean> = this.originsFacade.isOdServiceDown;
  openDatePicker$: Observable<boolean>;

  allFilteredAirports$ = this.bookerFacade.allFilteredAirports$;

  recentAirports$ = this.bookerFacade.recentSearchesDeDuped$();

  destinations$: Observable<CountryWithAirports[]>;

  showValidationErrors = false;
  submittingSearch = false; // submit is in progress

  removeRouteFiltering: boolean;

  // air
  isSharedMarket$;
  isInterlineRoute$: Observable<boolean>;
  isInternationalRoute$: Observable<boolean>;

  vm$ = this.bookerAirStore.vm$;

  bookerId: string;
  isDurableHomePage: boolean;
  constructor(
    public zone: NgZone,
    public fb: UntypedFormBuilder,
    public element: ElementRef,
    private cookieStorageService: CookieStorageService,
    private routerFacade: RouterFacade,
    public originsFacade: OriginsFacade,
    public windowService: WindowService,
    // global store for booker is preserved in localstorage
    public bookerFacade: BookerFacade,
    private analyticsService: AnalyticsService,
    private readonly bookerMultiCityStore: BookerMultiCityStore,
    private dialogService: JbDialogService,
    private rwbABTestingService: RWBABTestingService,
    public appConfigService: AppConfigService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    // local component state is not in localstorage
    private readonly bookerAirStore: BookerAirStore,
    private formSubmitterService: FormSubmitterService,
    private analyticsFacade: AnalyticsFacade,
    @Inject(WINDOW) private window,
    private textContentService: TextContentService,
    private globalFacade: GlobalFacade,
  ) {
    this.bookerId = BookerType.air + generateUniqueId();
    this.isSharedMarket$ = this.form
      .get(['flights', '0'])
      .valueChanges.pipe(
        map(
          ({ origin, destination }) =>
            (!!origin?.interline && !!origin.jb) ||
            (!!destination?.interline && !!destination?.jb),
        ),
      );

    this.isInternationalRoute$ = getControlVal(this.form.get('tripType')).pipe(
      distinctUntilChanged(),
      switchMap(tripType =>
        tripType === BOOKER_TRIP_TYPES.MULTICITY
          ? this.bookerMultiCityStore.hasInternationalFlight$
          : getControlVal(this.form.get(['flights', '0'])).pipe(
              map(({ origin, destination }) =>
                !!origin && !!destination
                  ? isInternational(origin, destination)
                  : false,
              ),
            ),
      ),
    );

    this.isInterlineRoute$ = getControlVal(this.form.get('tripType')).pipe(
      distinctUntilChanged(),
      switchMap(tripType =>
        tripType === BOOKER_TRIP_TYPES.MULTICITY
          ? this.bookerMultiCityStore.hasInterlineFlight$
          : getControlVal(this.form.get(['flights', '0'])).pipe(
              map(
                ({ origin, destination }) =>
                  !!origin?.interline || !!destination?.interline,
              ),
            ),
      ),
    );

    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(),
        switchMap(() => this.showFormErrors$),
        map(showFormErrors =>
          showFormErrors ? this.getAllErrorsFromForm(this.form) : {},
        ),
        debounceTime(100),
        distinctUntilChanged(equals),
      )
      .subscribe(errorsOrClearObject => {
        this.updateErrorMessages(errorsOrClearObject);
      });

    // on load TODO: turn into a function or operator for easy reuse
    // waits a second after we get back our airports, to make sure the data to populate the form is in from the server
    // todo: find a more elegant solution - removing this will have this subscribe
    //  fire on app load before data, giving bad local storage info
    combineLatest([
      this.originsFacade.airportsMap.pipe(filter(Boolean), delay(1000)),
      this.bookerFacade.currentOrigin$().pipe(filter(Boolean), delay(1000)),
    ])
      .pipe(
        // this will run one second after form initialization
        switchMap(() =>
          // this filter will always be true since we updated form with localstorage
          this.form.valueChanges.pipe(filter(() => !this.form.pristine)),
        ),
        // this block runs when booker form change
        // because the 1 sec delay above, any change before that sec are not detected
        map((formData: BookerFormState) =>
          formDataToSimpleAirSearchData(formData, BOOKER_CONFIG),
        ),
        // this filter is already executed above
        // filter(() => !this.form.pristine), // waits until form has been modified before trying to save
        take(1),
      )
      .subscribe((formData: RecentAirSearch) => {
        if (this.form.get('tripType').value !== BOOKER_TRIP_TYPES.MULTICITY) {
          // updates localstorage 1st time user changes booker
          this.bookerFacade.setRecentAirSearches(formData);
          // formData was saved in localstorage
          this.isFormDataSet = true;
        }
        if (this.saveToUrlParam) {
          // updates the url parameters on form change
          // todo: potentially move into a facade or a separate service
          this.router.navigate([], {
            relativeTo: activatedRoute,
            queryParams: recentSearchToUrlParam(formData),
            queryParamsHandling: 'merge',
          });
        }
      });

    // this updates local storage on change, instead of the previous onSubmit
    // todo: could be made to be more efficient
    this.form.valueChanges
      .pipe(
        takeUntilDestroyed(),
        debounceTime(300),
        // touched is updated only after valueChanges event completes;
        // hence touched & skip is replaced with isFormDataSet; so booker properly updates localstorage
        // filter(() => this.form.touched), // waits until form is interacted with before trying to save
        // skip(1), // ignore the first update - the first update is handled above
        filter(() => this.isFormDataSet),
        map(formData => formDataToSimpleAirSearchData(formData, BOOKER_CONFIG)),
        switchMap(recentAirSearch =>
          this.bookerFacade.recentAirSearches.pipe(
            take(1),
            filter(
              localStorageRecent =>
                JSON.stringify(localStorageRecent[0]) !==
                JSON.stringify(recentAirSearch),
            ),
            map(() => recentAirSearch),
          ),
        ),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
        ),
        // waits a second after we get back our airports, to make sure the data to populate the form is in from the server
        // todo: find a more elegant solution - removing this will have this subscribe fire on app load before data, giving bad local storage info
        skipUntil(this.allFilteredAirports$.pipe(switchMap(() => timer(1000)))),
        withLatestFrom(
          this.bookerFacade.prioritizedPromoCodeWithSource$(this.promoCode$),
        ),
      )
      .subscribe(([formData, promoCodeData]) => {
        // todo: when we better align multicity with non multicity, we can remove things like this
        if (this.form.get('tripType').value !== BOOKER_TRIP_TYPES.MULTICITY) {
          const updatedFormData = {
            ...formData,
            promoCode: promoCodeData.source === 'url' ? '' : formData.promoCode,
          };
          // replaces previous user search in localstorage with current search
          this.bookerFacade.updateCurrentAirSearch(updatedFormData);
        }
        if (this.saveToUrlParam) {
          // updates the url parameters on form change
          // todo: potentially move into a facade or a separate service
          this.router.navigate([], {
            relativeTo: activatedRoute,
            queryParams: recentSearchToUrlParam(formData),
            queryParamsHandling: 'merge',
          });
        }
      });

    combineLatest([
      this.form.get('departureDate').valueChanges,
      this.form.get('returnDate').valueChanges,
    ])
      .pipe(
        takeUntilDestroyed(),
        debounceTime(30),
        withLatestFrom(this.bookerAirStore.bffIntegrationEnabled$),
        filter(([_, bffIntegrationEnabled]) => bffIntegrationEnabled),
      )
      .subscribe(([[departDate, returnDate]]) => {
        this.bookerAirStore.updateDepartDate(departDate);
        this.bookerAirStore.updateReturnDate(returnDate);
      });

    this.form
      .get('tripType')
      .valueChanges.pipe(takeUntilDestroyed())
      .subscribe(tripType => {
        this.bookerAirStore.updateBookerAirTripType(tripType);
      });

    combineLatest([
      this.form.get('bookWithPoints').valueChanges,
      this.form.get('travelers').valueChanges,
      this.form.get('tripType').valueChanges,
    ])
      .pipe(takeUntilDestroyed())
      .subscribe({
        next: formData => {
          const [fare, travelers, intineraryType] = formData;

          this.bookerAirStore.updateBookerAirFaresForm({
            intineraryType,
            fare: fare
              ? BOOKER_CONFIG.FARE_TYPES.POINTS
              : BOOKER_CONFIG.FARE_TYPES.DOLLARS,
            ADT: travelers.allTravelers.adults,
            CHD: travelers.allTravelers.children,
            INF: travelers.allTravelers.infants,
          });
        },
      });

    // flights needed to be separated out from the above combine latest because it cant have a
    // debounce time. Any delay in setting flights values in the store will result in the booker
    // air store requesting the wrong pricing data in the specific case of a user having after filled
    // out every booker field goes and updates the destination and focus is quickly set to start date
    // The debounce time prevents the new destination from being set and the wrong data is presented
    // to the user. See DOT-9868
    this.form
      .get(['flights', 0])
      .valueChanges.pipe(takeUntilDestroyed())
      .subscribe({
        next: ({ origin, destination }) => {
          this.bookerAirStore.updateOriginDestinationCode({
            originCode: origin ? [origin.code] : [],
            destinationCode: destination ? [destination.code] : [],
          });
        },
      });

    // todo: this could be changed in a structure where all flights are arrays, like we have now (2021-Feb-01)
    // Sync Booker and MultiCity forms on trip type changes.
    getControlVal(this.form.get('tripType'))
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(),
        pairwise(),
        withLatestFrom(this.bookerMultiCityStore.flight$(0)),
      )
      .subscribe(([[prevTripType, currentTripType], flight]) => {
        if (currentTripType === BOOKER_TRIP_TYPES.MULTICITY) {
          flight.patchValue({
            fromAirport: this.form.get(['flights', '0']).value?.origin,
            toAirport: this.form.get(['flights', '0']).value?.destination,
          });
          const departureDate = this.form.get('departureDate').value;
          if (departureDate) {
            flight.patchValue({ departureDate });
          }
        }
        if (prevTripType === BOOKER_TRIP_TYPES.MULTICITY) {
          this.form.get(['flights', '0']).patchValue({
            origin: flight.get('fromAirport').value,
            destination: flight.get('toAirport').value,
          });

          this.form.patchValue({
            departureDate: flight.get('departureDate').value,
          });
        }
        if (
          currentTripType === BOOKER_TRIP_TYPES.ROUNDTRIP &&
          this.form.get('returnDate').value &&
          this.form.get('departureDate').value >
            this.form.get('returnDate').value
        ) {
          this.form.patchValue({
            returnDate: null,
          });
        }
      });

    // todo: Move this out into an effect and refactor to be simpler
    const initialLoadData$ = combineLatest([
      this.originsFacade.airportsMap.pipe(filter(airportMap => !!airportMap)),
      this.originsFacade.countryByAirportMap,
      this.originsFacade.nearbyAirportCodes,
    ]).pipe(
      filter(res => res.every(item => !!item)),
      withLatestFrom(
        this.bookerFacade.originParam$,
        this.bookerFacade.destinationParam$,
        this.bookerFacade.recentAirSearches.pipe(map(pluckRecentAirport)),
        (
          combinedData,
          originParam: string | undefined,
          destinationParam: string | undefined,
          recentAirports,
        ) => [...combinedData, originParam, destinationParam, recentAirports],
      ),
      share(),
    );

    if (this.cookieStorageService.shouldEnableCaptcha()) {
      this.form.addControl(
        'recaptchaReactive',
        new UntypedFormControl(undefined, Validators.required),
      );
      // DO NOT REMOVE the zone here, it is a firefox fix
      // to show the captcha when history and data got removed
      this.zone.run(() => (this.showCaptcha = true));

      this.routerFacade.isHomePage.pipe(takeUntilDestroyed()).subscribe(() => {
        this.form.addControl(
          'recaptchaReactive',
          new UntypedFormControl(undefined, Validators.required),
        );
        // DO NOT REMOVE the zone here, it is a firefox fix
        // to show the captcha when history and data got removed
        this.zone.run(() => (this.showCaptcha = true));
      });
    }

    // TEMPORARY SOLUTION TO A DEPENDABLE EVENT FOR POST_INITIAL_VALUE SET ON THE FORM
    initialLoadData$
      .pipe(
        waitUntil(
          this.originsFacade
            .destinationAirports('BOOKER_AIR')
            .pipe(filter(destinations => !!destinations)),
        ),
        take(1),
      )
      .subscribe(() => {
        this.jbBookerInitialStateSet.emit(this.form.getRawValue());
      });

    // getControlVal(this.form.get(['flights', '0']))
    //   .pipe(
    //     takeUntilDestroyed(this.destroyed),
    //     debounceTime(100),
    //     map(
    //       (flight: { origin: Airport; destination: Airport }) =>
    //         flight.destination?.code,
    //     ),
    //     distinctUntilChanged(),
    //     skip(1),
    //     skipUntil(initialLoadData$.pipe(switchMap(() => timer(1000)))),
    //     withLatestFrom(this.bookerAirStore.bffIntegrationEnabled$),
    //     filter(([_, bffIntegrationEnabled]) => bffIntegrationEnabled),
    //   )
    //   .subscribe(() => {
    //     this.form.patchValue({
    //       departureDate: null,
    //       returnDate: null,
    //     });
    //
    //     /*
    //     Our current data-range-picker is not listening for any changes related to
    //     returnDate and returnDate
    //     In this case, we are creating this workaround to force update our date-range-picker.
    //     The recentDepartureDate$ and recentReturnDate$ are listening to the following Subject behavior.
    //     */
    //     this.updateRecentReturnDate$.next(null);
    //     this.updateRecentDepartureDate$.next(null);
    //   });
  }

  ngOnInit() {
    this.globalFacade.isCmsFailed
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe((cmsFailed: boolean) => (this.isDurableHomePage = cmsFailed));
    // using async await here broke somethings, so using a then call
    firstValueFrom(
      this.bookerData$.pipe(
        map(bookerData => bookerData.errorMessages.captchaInvalid),
      ),
    ).then(errorMessage => {
      this.captchaErrorMessage = errorMessage;
    });

    this.bookerFacade.bookerGlobalSettings$
      .pipe(takeUntilDestroyed(this._destroy))
      .subscribe(settings => {
        if (
          booleanStringCheck(settings.displayMessage) &&
          settings.messageContent?.length > 0
        ) {
          // if display message is on, it will override the settings from isStatic and the content
          this.isStatic = true;
          this.content = traverse(settings.messageContent[0].bodyText[0]);
        }
      });

    this.analyticsFacade.bookerStartEventCall(this.bookerId);
    this.origin$ = this.bookerFacade
      .currentOrigin$(
        false,
        this.originAirport$.pipe(),
        getControlVal(this.form.get(['flights', '0'])).pipe(
          map(flight => flight.origin),
          skip(1),
          filter(airport => !!airport),
        ),
      )
      .pipe(
        debounceTime(0),
        filter(airport => !!airport),
        waitUntil(this.vm$),
        // cutting down on the noise helps prevent some bugs,
        // todo: refine the function to inherently reduce unnecessary calls, without relying on distinctUntilChanged
        distinctUntilKeyChanged('code'),
        share(),
      );

    /**
     * Destination value to populate the booker air. If there are query parameters present that the booker uses
     * it will default the value unless overriden by query parameter specific for destination.
     */

    this.destination$ = this.withStaticDefault(
      this.bookerFacade
        .currentDestination$(
          this.destinationAirport$,
          getControlVal(this.form.get(['flights', '0'])).pipe(
            map(flight => flight.destination),
            filter(airport => !!airport),
          ),
        )
        .pipe(
          debounceTime(0),
          filter(airport => !!airport),
          waitUntil(this.vm$),
          distinctUntilKeyChanged('code'),
        ),
      BOOKER_CONFIG.DEFAULTS.destination,
      this.bookerFacade.destinationParam$.pipe(map(Boolean)), // boolean whether query param for destination present
    );
    this.destinations$ = this.removeRouteFiltering
      ? this.bookerFacade.allOriginsAsDestinations$(this.origin$)
      : this.bookerFacade.destinations$(this.origin$);

    /**
     * Traveller count value to populate the booker air. If there are query parameters present that the booker uses
     * it will default the value unless overriden by query parameter specific for Traveller count.
     */
    // gets travelers count from localstorage & observes when travelers count changes
    this.recentTravellerCount$ = this.withStaticDefault(
      this.bookerFacade.prioritizedTravellerCount$({
        topAdultPriority$: this.travelerCountAdult$,
        topChildPriority$: this.travelerCountChild$,
        topInfantPriority$: this.travelerCountInfant$,
        topTotalPriority$: getControlVal(this.form.get('travelers')).pipe(
          skip(1),
          startWith(null),
        ),
      }),
      BOOKER_CONFIG.DEFAULTS.travellers,
      this.bookerFacade.travellersInUrl$,
    );

    /**
     * Departure value to populate the booker air. If there are query parameters present that the booker uses
     * it will default the value unless overriden by query parameter specific for depature.
     * This recentDepartureDate$ will also keep listening to the stream updateRecentDepartureDate$.
     * If there are any event in this stream, the date will also be updated.
     * To summarize -
     * withStaticDefault: It will trigger on the first render.
     * updateRecentDepartureDate$: it will update the date when it is called.
     */
    this.recentDepartureDate$ = merge(
      this.bookerFacade.prioritizedDepartureDate$(this.departDate$).pipe(
        switchMap((dDate: string | Date) =>
          getControlVal(this.form.get('departureDate')).pipe(
            startWith(dDate),
            map(date => (typeof date === 'string' ? parseISO(date) : date)),
            filter(date => isValid(date)),
          ),
        ),
      ),
      this.updateRecentDepartureDate$.pipe(filter(date => isValid(date))),
    ).pipe(
      distinctUntilChanged((prev, curr) => isSameDay(prev, curr)),
      tap(date => console.log('Recent departure date updated:', date)),
    );

    // Separate stream for default value
    const defaultDepartureDate$ = of(BOOKER_CONFIG.DEFAULTS.departure).pipe(
      takeUntil(this.recentDepartureDate$.pipe(first())),
    );

    // Combine with default
    this.recentDepartureDate$ = merge(
      this.recentDepartureDate$,
      defaultDepartureDate$,
    );

    /**
     * Return value to populate the booker air. If there are query parameters present that the booker uses
     * it will default the value unless overriden by query parameter specific for return.
     * This recentReturnDate$ will also keep listening to the stream updateRecentReturnDate$.
     * If there are any event in this stream, the date will also be updated.
     * To summarize -
     * withStaticDefault: It will trigger on the first render.
     * updateRecentReturnDate$: it will update the date when it is called.
     */
    this.recentReturnDate$ = merge(
      this.bookerFacade.prioritizedReturnDate$(this.returnDate$).pipe(
        switchMap((rDate: string | Date) =>
          getControlVal(this.form.get('returnDate')).pipe(
            startWith(rDate),
            map(date => (typeof date === 'string' ? parseISO(date) : date)),
            filter(date => isValid(date)),
          ),
        ),
      ),
      this.updateRecentReturnDate$.pipe(filter(date => isValid(date))),
    ).pipe(
      distinctUntilChanged((prev, curr) => isSameDay(prev, curr)),
      tap(date => console.log('Recent return date updated:', date)),
    );

    // Separate stream for default value
    const defaultReturnDate$ = of(BOOKER_CONFIG.DEFAULTS.return).pipe(
      takeUntil(this.recentReturnDate$.pipe(first())),
    );

    // Combine with default
    this.recentReturnDate$ = merge(this.recentReturnDate$, defaultReturnDate$);

    this.promoCodeWithSource$ = this.bookerFacade
      .prioritizedPromoCodeWithSource$(this.promoCode$)
      .pipe(
        map(code =>
          // ignore promoCode if cms sets it to false
          // if param => always show the field and put the value
          {
            return this.contentVar.showAirPromoCode === 'true' ||
              code.source === 'url'
              ? code
              : { ...code, promoCode: '' };
          },
        ),
        switchMap(
          (prioritizedPromoCodeWithSource: {
            promoCode: string;
            source: 'url' | 'recent' | 'input';
          }) =>
            getControlVal(this.form.get('promoCode')).pipe(
              skip(1),
              startWith(prioritizedPromoCodeWithSource),
            ),
        ),
      );

    this.evaluatedIsTrueBlue$ = this.withStaticDefault(
      this.bookerFacade.prioritizedIsTrueBlue$(this.isTrueBlue$),
      BOOKER_CONFIG.DEFAULTS.trueblue,
      this.bookerFacade.fareTypeInUrl$.pipe(map(Boolean)),
    );

    /**
     * Trip Type value to populate the booker air. If there are query parameters present that the booker uses
     * it will default the value unless overriden by query parameter specific for Trip Type.
     */
    this.evaluatedTripType$ = this.withStaticDefault(
      this.bookerFacade.prioritizedTripType$(this.tripType$),
      BOOKER_CONFIG.DEFAULTS.tripType,
      this.bookerFacade.tripTypeInUrl$.pipe(map(Boolean)),
    );

    // deals with the initial value not being properly set in form, todo: resolve in template, potentially by making a wrapper around
    // the radio/select/checkbox component
    zip(this.evaluatedTripType$, this.evaluatedIsTrueBlue$)
      .pipe(
        take(1), // initialize triptype only first time
      )
      .subscribe(([tripType = 'RT', bookWithPoints]) => {
        // initialize triptype in booker form with value from localstorage
        this.updateBookerForm(this.form, { tripType, bookWithPoints });
      });

    // fires a new request for destinations whenever origin changes
    this.origin$
      .pipe(
        takeUntilDestroyed(this._destroy),
        filter(airport => !!airport),
        distinctUntilKeyChanged('code'),
      )
      .subscribe(airport => {
        this.originsFacade.requestDestinationAirports(
          airport.code,
          'BOOKER_AIR',
        );
      });

    // this takes the multiCity input values, if they exist, combines them with the rest of the trip data, and sets it to pre-populated
    // this is an easy way to hook into the already existing 'autofill' flow to allow a dev to pass in multiCity data
    combineLatest([
      this.airportMap$.pipe(filter(hashMap => hashMap && !isEmpty(hashMap))),
      this.evaluatedTripType$,
      this.multiFlights$,
      this.recentTravellerCount$ as Observable<{
        ADT: number;
        CHD: number;
        INF: number;
      }>,
      this.evaluatedIsTrueBlue$,
      this.bookerFacade.recentAirSearches,
    ])
      .pipe(
        filter(
          ([airportMap, tripType]) =>
            !!airportMap && tripType === BOOKER_TRIP_TYPES.MULTICITY,
        ),
        take(1),
      )
      .subscribe(
        ([
          airportMap,
          tripType,
          multiFlights,
          recentTravelerCount,
          isTrueBlue,
          recentSearches,
        ]) => {
          if (multiFlights?.length) {
            const recentAirSearchFromMultiCityInput: RecentAirSearch = {
              ...recentTravelerCount,
              departDate: multiFlights.map(flight => flight.departDate),
              destinationCode: multiFlights.map(flight => flight.destination),
              fare: isTrueBlue ? 'tb' : 'usd',
              intineraryType: tripType,
              originCode: multiFlights.map(flight => flight.origin),
            };
            this.bookerMultiCityStore.setPrePopulatedSearch(
              [recentAirSearchFromMultiCityInput],
              airportMap,
            );
          } else {
            this.bookerMultiCityStore.setPrePopulatedSearch(
              recentSearches,
              airportMap,
            );
          }
        },
      );

    this.bookerAirStore.bookerReady$
      .pipe(debounceTime(1000), take(1))
      .subscribe(() => {
        this.analyticsFacade.bookerLoadDoneEventCall(this.bookerId);
      });
  }

  setButtonTheme() {
    this.buttonThemeVar =
      this.buttonThemeVar ||
      BOOKER_SUBMIT_BUTTON_THEME_MAP[
        this.contentVar?.bookMessages?.buttonTheme
      ] ||
      'ctaSecondary';
  }

  createForm(config?: Partial<BookerFormState>): UntypedFormGroup {
    return createAirBookerFormV2(this.fb, config);
  }

  applyValidation(formState: BookerFormState): void {
    // capcha validation
    if (this.showCaptcha) {
      setCaptchaValidation(
        this.form.get('recaptchaReactive'),
        // this needs to be changed with content
        this.captchaErrorMessage,
      );
    }
  }

  handleBookerSubmit(
    rawValue: BookerFormState,
    multiCityFlights: BookerFlight[] = [],
  ): void {
    const submitData = {
      ...rawValue,
      flights: multiCityFlights?.length ? multiCityFlights : rawValue.flights,
    };

    const submitEvent = new CustomEvent('JbBookerSubmit', {
      detail: submitData || 'data unavailable',
      bubbles: true,
      cancelable: true,
    });
    // todo: This may be unnecessary, with how angular elements handles emitting events, potentially remove
    this.element.nativeElement.dispatchEvent(submitEvent);

    this.jbBookerSubmit.emit(submitData);
  }

  updateBookerForm(
    form: AbstractControl,
    updatedData: RecursivePartial<BookerFormState>,
  ): void {
    form.patchValue(updatedData);
  }

  // MIGRATED FROM ABSTRACT CLASS

  onSubmit(isMobile: boolean): void {
    this.form.get('travelers').updateValueAndValidity();
    this.form.get('departureDate').updateValueAndValidity();
    this.showFormErrors$.next(false);
    this.applyValidation(this.form.getRawValue());
    showValidation(this.form);
    // deferring the call until all validations are checked
    // todo: setup async validators to better handle this
    requestAnimationFrame(() => {
      if (this.form.valid) {
        const formJS = this.form.getRawValue();
        const warningsList = getAirBookerWarnings(
          formJS.flights[0].origin,
          this.appConfigService.bookerRestrictedOriginsModalWarning,
        );

        !warningsList.length
          ? this.submit(
              this.form.getRawValue(),
              isMobile,
              this.disableAutoSubmit === 'false'
                ? false
                : Boolean(this.disableAutoSubmit),
            )
          : this.openDialog(this.contentVar, warningsList, isMobile);
      } else {
        this.showFormErrors$.next(true);
        if (!isMobile) {
          const errorObject = this.getAllErrorsFromForm(this.form);
          this.updateErrorMessages(errorObject);
          this.focusOnAppropriateError(errorObject);
        }
        this.analyticsService.trackEvent('Page Validation Error', {
          pageValidationError: retrieveAllErrorMessages(
            this.form,
            BOOKER_CONFIG.BOOK_ERROR_MESSAGE_CONFIG,
          ).join(', '),
        });
      }
    });
  }

  openDialog(
    response: BookerResponse,
    warningList: WarningMessage[],
    isMobile: boolean,
    flightsData?: BookerFlight[],
    formData?: BookerFormState,
    wasMultiCitySubmit: Boolean = false,
  ) {
    // Opening dialog and setting to constant to be subscribed to later
    const componentRef = this.dialogService.openDialog(
      JBBookerAirWarningDialogComponent,
    );

    // Sending data to the dialog component
    componentRef.handleData(response.warningMessages, warningList);

    // Receiving/subscribing to data from the dialog component
    componentRef.shouldContinue$.pipe(take(1)).subscribe(shouldContinue => {
      this.submittingSearch = shouldContinue;
      // todo: make disableAutoSubmit potentially set by a setting to a private value, handling the formatting
      if (shouldContinue) {
        if (wasMultiCitySubmit) {
          this.updateMultiCityLocalStorageAndSubmit(
            formData,
            flightsData,
            isMobile,
          );
        } else {
          this.submit(
            this.form.getRawValue(),
            isMobile,
            this.disableAutoSubmit === 'false'
              ? false
              : Boolean(this.disableAutoSubmit),
            flightsData,
          );
        }
      }
      // Have to close here in parent to ensure subscription comes thru
      this.dialogService.closeDialog();
    });
  }

  // todo: the disableSubmit functionality is more or less unnecessary now, but needs a refactor to remove
  // copied the submit logic to recent-searches.service, Look at this file also when refactoring this method.
  submit(
    formData: BookerFormState,
    isMobile: boolean,
    disableSubmit: boolean = false,
    multiCityFlights: BookerFlight[] = [],
  ) {
    this.handleBookerSubmit(formData, multiCityFlights);
    // saves submitted search in localStorage
    this.bookerFacade.setSubmittedAirSearches(formData);

    // this could be a bit more elegant, but when we have multicity flights
    // we only care about it's base flight - currently multicity non multicity flights
    // are not synchronized as nicely as I would like, will be better when we have
    // onchange updates for multicity - todo: simplify when that happens
    const baseFlightToUse = multiCityFlights?.length
      ? {
          origin: multiCityFlights[0].fromAirport,
          destination: multiCityFlights[0].toAirport,
        }
      : formData.flights[0];

    // set recent search cookie, just origin for now
    // TODO: First thing to do for refactor, change recent search to be done on field change
    this.cookieStorageService.setCookie(
      'recentAirSearch',
      baseFlightToUse.origin.code,
    );

    // only send FS event for non-multicity flights
    if (!multiCityFlights?.length) {
      sendBookerFSEvent(
        this.window,
        formData.tripType,
        formData.bookWithPoints,
        baseFlightToUse.origin,
        baseFlightToUse.destination,
        formData.departureDate,
        formData.returnDate,
        formData.travelers.allTravelers,
        this.isDurableHomePage,
      );
    }
    // todo: can move around disable submit to wrap all of this logic, later date
    const NGBABTestOnCheck = Boolean(
      this.rwbABTestingService.getRwbBookerUrl(),
    );

    if (NGBABTestOnCheck && !disableSubmit) {
      const vanillaFormData = createVanillaFormData(
        {
          ...formData,
          origin: baseFlightToUse.origin,
          destination: baseFlightToUse.destination,
        },
        this.cookieStorageService.getCountryCodeCookie(),
        multiCityFlights,
      );
      const querystring = convertBookerFormDataToQueryString(
        vanillaFormData,
        multiCityFlights,
      );
      this.windowService.navigateToOutsideUrl(
        `${this.appConfigService.rwbBookerUrl}?${querystring}`,
      );
    } else if (isMobile && !multiCityFlights?.length && !disableSubmit) {
      const mobileUrl = createMobileBookingUrlMainBooker(
        this.appConfigService.baseBook,
        this.appConfigService.mobileBaseUrl,
        this.appConfigService.isProdEnv,
        formData,
      );
      this.windowService.navigateToOutsideUrl(mobileUrl);
    } else if (!disableSubmit) {
      const vanillaFormData = createVanillaFormData(
        {
          ...formData,
          origin: baseFlightToUse.origin,
          destination: baseFlightToUse.destination,
        },
        this.cookieStorageService.getCountryCodeCookie(),
        multiCityFlights,
      );
      this.formSubmitterService.submitForm(
        vanillaFormData,
        `${this.appConfigService.baseBook}/B6/webqtrip.html`,
      );
    }
  }

  isSubmittingSearch(event): void {
    this.submittingSearch = event;
  }

  handleCityPairChange(
    airport: Airport,
    changedAirport: 'origin' | 'destination',
  ) {
    // Form values are not getting programatically updated, need a slight delay
    requestAnimationFrame(() => {
      if (changedAirport === 'origin') {
        const destinationInvalid = isCityPairRestricted(
          airport?.code,
          this.form.get(['flights', '0']).value.destination?.code,
        );
        if (destinationInvalid) {
          this.form
            .get(['flights', '0'])
            .setValue({ origin: airport, destination: undefined });
        }
      }

      if (changedAirport === 'destination') {
        const originInvalid = isCityPairRestricted(
          airport?.code,
          this.form.get(['flights', '0']).value.origin?.code,
        );
        if (originInvalid) {
          this.form
            .get(['flights', '0'])
            .setValue({ origin: undefined, destination: airport });
        }
      }
    });
  }

  // todo: type, and maybe write a more versatile function - for now getting errors off forms,
  //  but might want to avoid that pattern eventually
  getAllErrorsFromForm(form: UntypedFormGroup): BookerFormErrorObject {
    return {
      captchaError: Object.values(
        form.get('recaptchaReactive').errors || {},
      )[0],
      originError: form.get(['flights', '0']).errors?.originError,
      destinationError: form.get(['flights', '0']).errors?.destinationError,
      departureDateError: Object.values(
        form.get('departureDate').errors || {},
      )[0],
      returnDateError: Object.values(form.get('returnDate').errors || {})[0],
      travellersError: Object.values(form.get('travelers').errors || {})[0],
    };
  }

  updateErrorMessages(
    errorMessageObject: Partial<BookerFormErrorObject>,
  ): void {
    this.originAirportError$.next(errorMessageObject.originError);
    this.destinationAirportError$.next(errorMessageObject.destinationError);
    this.departureDateError$.next(errorMessageObject.departureDateError);
    this.returnDateError$.next(errorMessageObject.returnDateError);
    this.travelerCountError$.next(errorMessageObject.travellersError);
  }

  focusOnAppropriateError(errorMessageObject: BookerFormErrorObject): void {
    if (errorMessageObject.originError) {
      this.bookerV2Component.focusOnOriginAirportField();
      return;
    }
    if (errorMessageObject.destinationError) {
      this.bookerV2Component.focusOnDestinationAirportField();
      return;
    }
    if (errorMessageObject.departureDateError) {
      this.bookerV2Component.focusOnDepartureField();
      return;
    }
    if (errorMessageObject.returnDateError) {
      this.bookerV2Component.focusOnReturnField();
      return;
    }
    if (errorMessageObject.travellersError) {
      this.bookerV2Component.focusOnPassengersField();
    }
    if (errorMessageObject.captchaError) {
      this.bookerV2Component.focusOnCaptchaField();
    }
  }

  updateMultiCityLocalStorage(
    bookerForm: BookerFormState,
    flights: BookerFlight[],
  ) {
    const currentSearch: RecentAirSearch = {
      originCode: flights.map(flight => flight.fromAirport.code),
      destinationCode: flights.map(flight => flight.toAirport.code),
      departDate: flights.map(flight =>
        parseISO(getAppropriateDateString(flight.departureDate)).toISOString(),
      ),
      intineraryType: bookerForm.tripType,
      ADT: bookerForm.travelers.allTravelers.adults,
      CHD: bookerForm.travelers.allTravelers.children,
      INF: bookerForm.travelers.allTravelers.infants,
      fare: bookerForm.bookWithPoints ? 'tb' : 'usd',
    };
    this.bookerFacade.setRecentAirSearches(currentSearch);
  }

  updateMultiCityLocalStorageAndSubmit(
    formData: BookerFormState,
    flightsData: BookerFlight[],
    isMobile: boolean,
  ) {
    this.updateMultiCityLocalStorage(formData, flightsData);

    // todo: update the input for this.disableAutoSubmit to automatically
    //  convert to boolean
    const disableAutoSubmit =
      this.disableAutoSubmit === 'false'
        ? false
        : Boolean(this.disableAutoSubmit);
    this.submit(formData, isMobile, disableAutoSubmit, flightsData);
  }

  onMultiCitySubmit(
    multiCityFlights: FormArray<FormGroup<BookerFlightWithControls>>,
    bookerForm: FormGroup<BookerFormStateWithControls>,
    isMobile: boolean,
  ) {
    multiCityFlights.controls.forEach(control => {
      control.get('departureDate')?.updateValueAndValidity();
    });
    if (multiCityFlights?.valid && bookerForm.get('travelers').valid) {
      const multiCityDateStrings = multiCityFlights.value
        .filter(flight => !!flight.departureDate)
        .map(flight => getAppropriateDateString(flight.departureDate));

      const formData = bookerForm.getRawValue();
      const x = formData.departureDate;
      // Allow user to submit when there are blank flights after the ones that are mandatory (1 and 2)
      const flightsData = multiCityFlights
        .getRawValue()
        .filter(
          flight =>
            !!flight.departureDate &&
            !!flight.fromAirport &&
            !!flight.toAirport,
        );

      const warnings = getMultiCityBookerWarnings(
        flightsData.map(flight => flight.fromAirport),
        this.appConfigService.bookerRestrictedOriginsModalWarning,
      );

      if (!!warnings.length) {
        this.openDialog(
          this.contentVar,
          warnings,
          isMobile,
          flightsData,
          formData,
          true,
        );
      } else {
        this.updateMultiCityLocalStorageAndSubmit(
          formData,
          flightsData,
          isMobile,
        );
      }
    } else {
      this.submittingSearch = false;
      this.showValidationErrors = true;
      const errorObject = this.getAllErrorsFromForm(this.form);
      this.updateErrorMessages(errorObject);
    }
  }

  /**
   * Determine if a default value or the value from a the normal flow should be return based on the
   * present query parameters.
   * @param observableValue$ Value return from normal flow
   * @param defaultStaticValue Static value return when default required
   * @param hasQParam$ Indicator that query param is present in url
   * @returns Either the value from the normal flow or the default;
   */
  withStaticDefault(
    observableValue$: Observable<any>,
    defaultStaticValue: any,
    hasQParam$: Observable<boolean>,
  ): Observable<any> {
    return combineLatest([
      observableValue$,
      this.bookerFacade.flightsBookerUseDefaults$,
      hasQParam$,
    ]).pipe(
      map(([value, useDefault, hasQParam]) => {
        if (useDefault && !hasQParam) {
          // if default is required and doesnt have a qparam to override default return default
          return defaultStaticValue;
        }

        // default could be selected, but has qparam to override. Or no default, normal flow.
        return value;
      }),
    );
  }
}

export const bookerAirContainerResolver = (_, data) => {
  const bookerData = transformDynamicBookerData(data, 'bookerAir');
  return {
    isStatic: !!bookerData?.bodyText,
    content: bookerData?.bookerData || bookerData?.bodyText,
  };
};
